import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useCustomerCategories } from "@/hooks/useCustomerCategories";
import { useActivityTypes } from "@/hooks/useActivityTypes";
import { useSavedCards } from "@/hooks/useSavedCards";
import AddPaymentMethodInline from "@/components/payment/AddPaymentMethodInline";
import {
  computeActivityJoinAmountMinor,
  computeActivityJoinBreakdownMinor,
} from "@/lib/computeActivityJoinAmountMinor";
import { isJoinConfirmDisabled } from "@/lib/joinConfirmGate";
import { chargePendingPayment } from "@/lib/chargePendingPayment";
import type { PricingRule, SpecialPriceDate } from "@/lib/pricing";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPrice } from "@/utils/formatPrice";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { Users, MapPin, Clock, UserPlus, LogOut } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import ShareButton from "@/components/shared/ShareButton";
import type { Tables } from "@/integrations/supabase/types";
import { frozenDate, frozenDateTime } from "@/lib/notificationDisplay";

// ─── Types ─────────────────────────────────────────────────────
interface OpenMatchPlayer {
  id: string;
  user_id: string;
  status: string;
}

// SPÅR 2-Pre: open_matches.booking_id dropped. Linked booking now via
// reverse-FK bookings.activity_id. Embed selects first matching booking.
interface LinkedBooking {
  id: string;
  price_total: number | null;
  duration_minutes: number | null;
}

interface OpenMatchWithRelations extends Tables<"activities"> {
  courts: { name: string } | null;
  activity_players: OpenMatchPlayer[];
  // Derived from bookings JOIN (post-Spår-2-Pre canonical direction)
  linked_bookings?: LinkedBooking[] | null;
  // Convenience getter — first linked booking's id, replaces removed
  // open_matches.booking_id column.
  booking_id?: string | null;
}

interface ProfileInfo {
  id: string;
  full_name: string | null;
  level: number | null;
  avatar_url: string | null;
}

// ─── Activity type config (fallback for unknown types) ───────────
const FALLBACK_BADGE = { icon: "✨", label: "Annat", color: "#888888", badgeClass: "bg-muted text-foreground border-border" };

// ─── Price display subcomponent ──────────────────────────────────
// Display/i18n M1: every system-mode price on the card renders from the CANONICAL breakdown
// (computeActivityJoinBreakdownMinor) — the SAME computation that charges. This replaced the two
// parallel paths (flat: ceil(booking.price_total/spots)+kr-fee [court-dropped for v2 rows, L51];
// individual: usePlayerPrice+kr-fee [toggle-blind]). court + fee = total by construction, öre.
// A court-less activity gets the ZERO breakdown (charge creates no payment row) → "Gratis".
function ActivityPriceDisplay({ match, userId }: { match: OpenMatchWithRelations; userId: string | undefined }) {
  const { t } = useTranslation("activities");
  const { organization } = useOrganization();
  const { hallId } = useOrgHall();
  const { categories } = useCustomerCategories(organization?.id);

  const { data: pricingRules = [], isSuccess: matchRulesReady } = useQuery<PricingRule[]>({
    queryKey: ["pricing-rules-for-match", hallId],
    queryFn: async () => {
      if (!hallId) return [];
      const { data } = await supabase
        .from("pricing_rules")
        .select("*")
        .eq("hall_id", hallId);
      return (data ?? []) as PricingRule[];
    },
    enabled: !!hallId,
  });

  // Root-cause C gates (L56): never compute with unsettled inputs; rules.length in the key
  // forces a re-run on a 0→N rules load.
  const { data: breakdown } = useQuery({
    queryKey: ["activity-join-breakdown", match.id, userId, organization?.id, hallId, pricingRules.length],
    enabled:
      match.payment_mode === "system" && !!userId && !!hallId && !!organization?.id && matchRulesReady,
    queryFn: () =>
      computeActivityJoinBreakdownMinor({
        activity: {
          id: match.id,
          payment_mode: match.payment_mode,
          spots_total: match.spots_total,
          organizer_fee_per_person_minor: match.organizer_fee_per_person_minor,
          apply_member_discount: match.apply_member_discount,
          date: match.date,
          start_time: match.start_time,
          end_time: match.end_time,
        },
        userId,
        orgId: organization?.id,
        hallId,
        pricingRules,
        categories,
        hasLinkedBooking: !!match.booking_id,
      }),
  });

  // Handle external and free payment modes (info-strings; charge is 0 by design)
  if (match.payment_mode === 'free') {
    return <div className="text-xs text-muted-foreground">{t("interestSignup")}</div>;
  }
  if (match.payment_mode === 'external') {
    const displayPrice = match.display_price ?? match.price_per_person ?? 0;
    return (
      <div className="text-xs text-muted-foreground">
        {displayPrice > 0
          ? t("externalCardWithPrice", { amount: formatPrice(displayPrice) })
          : t("externalCardNoPrice")}
      </div>
    );
  }

  // System mode — canonical breakdown (loading → skeleton; 0 → genuinely free, e.g. court-less)
  if (!breakdown || !userId) {
    return <Skeleton className="h-4 w-24" />;
  }
  if (breakdown.totalMinor === 0) {
    return <div className="text-xs text-muted-foreground">{t("free")}</div>;
  }
  return (
    <div className="space-y-0.5">
      <span className="text-sm font-medium">
        {t("cardPriceForYou", { amount: formatPrice({ valueMinor: breakdown.totalMinor, currency: "SEK" }) })}
      </span>
      <div className="text-xs text-muted-foreground">
        {breakdown.feeMinor > 0
          ? t("cardBreakdownCourtFee", {
              court: formatPrice({ valueMinor: breakdown.courtShareMinor, currency: "SEK" }),
              fee: formatPrice({ valueMinor: breakdown.feeMinor, currency: "SEK" }),
            })
          : t("cardBreakdownCourtOnly", {
              court: formatPrice({ valueMinor: breakdown.courtShareMinor, currency: "SEK" }),
            })}
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────
interface OpenMatchListProps {
  dateFilter?: string;
  levelFilter?: boolean;
  typeFilter?: string;
  /**
   * Filter by multiple match_types using `IN (...)`. Mutually exclusive with
   * `typeFilter` (single-value `=` filter); if both are supplied, both apply
   * and Postgres returns rows matching both filters.
   * Used by Play.tsx Tournaments tab to render admin-organized activity types
   * (americano, vinnarbana, mexicano) alongside the bracket-tournaments list.
   */
  typeFilterIn?: string[];
  onlyJoinable?: boolean;
  excludeMatchType?: boolean;
}

export default function OpenMatchList({ dateFilter, levelFilter, typeFilter, typeFilterIn, onlyJoinable, excludeMatchType }: OpenMatchListProps) {
  const { t } = useTranslation("activities");
  const { user, profile } = useAuth();
  const { organization } = useOrganization();
  const { hallId } = useOrgHall();
  const { categories } = useCustomerCategories(organization?.id);
  const { types: activityTypes } = useActivityTypes();
  const qc = useQueryClient();
  const [confirmMatch, setConfirmMatch] = useState<OpenMatchWithRelations | null>(null);
  // 2d-ii Step 2: a no-card player joining a system-mode activity can save a card inline in the
  // confirm dialog (reused add-card flow). NO charge yet — that is Step 4. saveCard invalidates the
  // saved-cards query, so hasMitCard flips automatically once a card is saved.
  const { cards: savedCards } = useSavedCards();
  const hasMitCard = savedCards.some((c) => c.is_mit_enabled);

  // Build a lookup map from dynamic activity types
  const activityTypeMap = useMemo(() => {
    const map = new Map<string, { icon: string; label: string; color: string; badgeClass: string }>();
    for (const t of activityTypes) {
      map.set(t.slug, {
        icon: t.icon,
        label: t.name,
        color: t.color,
        badgeClass: `border-[${t.color}]/20 text-[${t.color}]`,
      });
    }
    return map;
  }, [activityTypes]);

  // ─── Pricing rules for join mutation ───
  const { data: pricingRules = [], isSuccess: pricingRulesReady } = useQuery<PricingRule[]>({
    queryKey: ["pricing-rules-for-join", hallId],
    queryFn: async () => {
      if (!hallId) return [];
      const { data } = await supabase.from("pricing_rules").select("*").eq("hall_id", hallId);
      return (data ?? []) as PricingRule[];
    },
    enabled: !!hallId,
  });

  const { data: matches = [], isLoading } = useQuery({
    queryKey: ["activities", organization?.id, dateFilter, typeFilter, typeFilterIn?.join(",") ?? ""],
    queryFn: async () => {
      let query = supabase
        .from("activities")
        .select("*, courts(name), activity_players(id, user_id, status), linked_bookings:bookings!activity_id(id, price_total, duration_minutes)")
        .eq("organization_id", organization!.id)
        .in("status", ["open", "full"])
        .gte("date", new Date().toISOString().split("T")[0])
        .order("date", { ascending: true })
        .order("start_time", { ascending: true });

      if (dateFilter) query = query.eq("date", dateFilter);
      if (typeFilter && typeFilter !== "all") query = query.eq("match_type", typeFilter);
      if (typeFilterIn && typeFilterIn.length > 0) query = query.in("match_type", typeFilterIn);
      if (excludeMatchType) query = query.neq("match_type", "match");
      const { data, error } = await query;
      if (error) throw error;
      // SPÅR 2-Pre: derive booking_id from linked_bookings JOIN
      const now = new Date();
      const filtered = (data ?? []).map((m: any) => ({
        ...m,
        booking_id: m.linked_bookings?.[0]?.id ?? null,
      })).filter((m: any) => {
        const endDT = new Date(`${m.date}T${m.end_time || "23:59"}`);
        return endDT > now;
      });
      return filtered as unknown as OpenMatchWithRelations[];
    },
    enabled: !!organization,
  });

  // 2d-ii Step 4.4: which of these activities has the current user PAID for? A paid join can't be
  // left in-app (leave_activity_v2 refuses it — refund = delspår 2e), so the card "Lämna" button is
  // disabled for those. The matches query already loads linked_bookings (booking ids); one batch
  // query maps the user's PAID booking_payments back to match ids (mirrors the detail-page hasPaidJoin).
  const matchBookingPairs = matches.flatMap((m) =>
    (m.linked_bookings ?? []).map((b) => [b.id, m.id] as const),
  );
  // 2e-core: map paid match → the DRAWN amount_minor (refund display = the amount drawn, one source).
  const { data: paidJoinByMatch = {} } = useQuery({
    queryKey: ["my-paid-activity-joins", user?.id, matchBookingPairs.map(([b]) => b).join(",")],
    enabled: !!user?.id && matchBookingPairs.length > 0,
    queryFn: async () => {
      const bookingToMatch = new Map(matchBookingPairs);
      const { data: paid } = await supabase
        .from("booking_payments")
        .select("booking_id, amount_minor")
        .eq("user_id", user!.id)
        .eq("status", "paid")
        .in("booking_id", [...bookingToMatch.keys()]);
      const map: Record<string, number | null> = {};
      for (const p of paid ?? []) {
        const row = p as { booking_id: string; amount_minor: number | null };
        const mid = bookingToMatch.get(row.booking_id);
        if (mid) map[mid] = row.amount_minor;
      }
      return map;
    },
  });
  const paidJoinSet = new Set(Object.keys(paidJoinByMatch));

  // 2e-core Policy-modell A: is a given match still inside its frozen cancellation window?
  // cancellation_hours frozen on the activity (NULL → 24 defensively). Drives refund-leave gating.
  const withinCancellationWindow = (m: { date: string; start_time: string; cancellation_hours?: number | null }) => {
    if (!m.date || !m.start_time) return false;
    const startMs = new Date(`${m.date}T${m.start_time}`).getTime();
    const hours = m.cancellation_hours ?? 24;
    return Date.now() < startMs - hours * 3600_000;
  };

  // Fetch creator profiles
  const creatorIds = [...new Set(matches.map((m) => m.created_by))];
  const { data: creatorProfiles = [] } = useQuery({
    queryKey: ["creator-profiles", creatorIds.join(",")],
    queryFn: async () => {
      if (creatorIds.length === 0) return [];
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name, level, avatar_url").in("id", creatorIds);
      return (data ?? []) as ProfileInfo[];
    },
    enabled: creatorIds.length > 0,
  });
  const profileMap = Object.fromEntries(creatorProfiles.map((p) => [p.id, p]));

  // Realtime subscription
  useEffect(() => {
    if (!organization) return;
    const channel = supabase
      .channel("open-matches-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "activities" }, () => {
        qc.invalidateQueries({ queryKey: ["activities"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "activity_players" }, () => {
        qc.invalidateQueries({ queryKey: ["activities"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [organization, qc]);

  // Fetch hall info for auto-booking
  const { data: hallData } = useQuery({
    queryKey: ["hall-for-activities", organization?.id],
    queryFn: async () => {
      const { data } = await supabase.from("halls").select("id, auto_book_open_matches").eq("organization_id", organization!.id).limit(1).single();
      return data;
    },
    enabled: !!organization,
  });

  // ─── Canonical charge breakdown for the confirm dialog (M1) ───
  // ONE query feeds BOTH the dialog's price rows (Banhyra/Arrangörsavgift/Totalt) AND the
  // add-card/"Gå med" gates — display == gate == charge by construction (the joinMutation
  // computes the same breakdown's totalMinor). Replaced the parallel confirmBooking
  // (bookings.price_total, court-dropped for v2 rows per L51) + usePlayerPrice (toggle-blind)
  // + kr-fee-column paths. Root-cause C gates (L56): only compute when hallId + orgId + the
  // pricing-rules query SUCCEEDED; pricingRules.length in the key forces a re-run on 0→N load.
  const { data: confirmBreakdown } = useQuery({
    queryKey: ["confirm-join-breakdown", confirmMatch?.id, user?.id, organization?.id, hallId, pricingRules.length],
    enabled: !!confirmMatch && !!user?.id && !!hallId && !!organization?.id && pricingRulesReady,
    queryFn: () =>
      computeActivityJoinBreakdownMinor({
        activity: {
          id: confirmMatch!.id,
          payment_mode: confirmMatch!.payment_mode,
          spots_total: confirmMatch!.spots_total,
          organizer_fee_per_person_minor: confirmMatch!.organizer_fee_per_person_minor,
          apply_member_discount: confirmMatch!.apply_member_discount,
          date: confirmMatch!.date,
          start_time: confirmMatch!.start_time,
          end_time: confirmMatch!.end_time,
        },
        userId: user?.id,
        orgId: organization?.id,
        hallId,
        pricingRules,
        categories,
        hasLinkedBooking: !!confirmMatch?.booking_id,
      }),
  });
  const confirmWillCharge = (confirmBreakdown?.totalMinor ?? 0) > 0;

  const joinMutation = useMutation({
    mutationFn: async (matchId: string) => {
      // 2d-i: converged to join_activity_v2 — the atomic RPC does the activity_players
      // insert/reactivate + spots_filled bump + (system-mode) pending booking_payments row
      // in ONE transaction. user_id forced server-side; org-gate replicates RLS; re-join
      // idempotent. The fee-led join amount (öre) is computed client-side via
      // computeActivityJoinAmountMinor (Lesson 38: price in one place) and passed in.
      // NO charge — pending row only (charge = 2d-ii). The old margin-model split write
      // (price_per_person + hardcoded member discount, kr) is replaced by the fee-led helper.
      const match = matches.find((m) => m.id === matchId);

      const amountMinor = match
        ? await computeActivityJoinAmountMinor({
            activity: {
              id: match.id,
              payment_mode: match.payment_mode,
              spots_total: match.spots_total,
              organizer_fee_per_person_minor: match.organizer_fee_per_person_minor,
              apply_member_discount: match.apply_member_discount,
              date: match.date,
              start_time: match.start_time,
              end_time: match.end_time,
            },
            userId: user?.id,
            orgId: organization?.id,
            hallId,
            pricingRules,
            categories,
            hasLinkedBooking: !!match.booking_id,
          })
        : null;

      const { data: joinData, error: joinError } = await (supabase.rpc as any)("join_activity_v2", {
        p_activity_id: matchId,
        p_amount_minor: amountMinor,
      });
      if (joinError) {
        const msg = joinError.message || "";
        if (/already_joined/.test(msg)) throw new Error(t("errors.alreadyJoined"));
        if (/not_a_member_of_org/.test(msg)) throw new Error(t("errors.mustBeMember"));
        if (/activity_full/.test(msg)) throw new Error(t("errors.activityFull", { defaultValue: "Aktiviteten är full" }));
        throw new Error(msg || t("errors.couldNotJoin"));
      }

      // 2d-ii Step 4: charge the pending row off-session with the player's saved MIT card.
      // join_activity_v2 returns {activity_id, payment_id}; chargePendingPayment marks the row paid
      // on success (Lesson 38: same charge primitive as booking). No-card joins are gated in Step 4.3
      // (the "Gå med" button), so the charge path is ALWAYS saved-card here — we only charge when a
      // card exists (no checkout-redirect branch for join). Outcomes:
      //   paid    → pending→paid (in the helper); player stays joined.
      //   skipped → charge_enabled=false (Step 3) → leave the pending row, player stays (AP's 2d-i state).
      //   failed  → decline/network → roll back the join atomically (leave_activity_v2) + surface the error.
      const paymentId = (joinData as { payment_id?: string } | null)?.payment_id ?? null;
      const mitCard = savedCards.find((c) => c.is_mit_enabled && c.is_default) ?? savedCards.find((c) => c.is_mit_enabled);
      if (paymentId && mitCard?.id && user && organization) {
        const charge = await chargePendingPayment({
          userId: user.id,
          orgId: organization.id,
          amountMinor: amountMinor ?? 0,
          paymentId,
          savedCardId: mitCard.id,
          description: `Aktivitet: ${match?.activity_title ?? matchId}`,
          relatedEntityType: "activity",
          relatedEntityId: matchId,
        });
        if (charge.status === "failed") {
          await (supabase.rpc as any)("leave_activity_v2", { p_activity_id: matchId });
          qc.invalidateQueries({ queryKey: ["activities"] });
          throw new Error(charge.network ? t("errors.paymentFailedRetry") : (charge.error || t("errors.paymentFailed")));
        }
        // paid / skipped → player stays joined (helper marked paid, or charge intentionally off).
      }

      if (match) {
        const newFilled = (match.spots_filled ?? 1) + 1;
        const newStatus = newFilled >= match.spots_total ? "full" : "open";

        // Notify creator
        const playerName = profile?.full_name ?? "Någon";
        const playerLevel = profile?.level ?? "?";
        await supabase.from("notifications").insert({
          user_id: match.created_by,
          organization_id: organization!.id,  // PR-B: org-scope per Phase 1 RLS
          type: "match_join",
          title: "Ny spelare i din aktivitet!",
          body: `${playerName} (nivå ${playerLevel}) gick med i din aktivitet ${frozenDate(match.date)} ${match.start_time?.slice(0, 5)}.`,
          data: { activity_id: matchId },
        });

        // Auto-book court when match becomes full
        if (newStatus === "full" && !match.booking_id && hallData?.auto_book_open_matches !== false) {
          const matchStart = new Date(`${match.date}T${match.start_time}`);
          const matchEnd = new Date(`${match.date}T${match.end_time}`);
          const durationMin = Math.round((matchEnd.getTime() - matchStart.getTime()) / 60000);

          const { data: courts } = await supabase.from("courts").select("id, name").eq("hall_id", hallData!.id).eq("status", "active");
          const { data: existing } = await supabase.from("bookings").select("court_id").eq("status", "confirmed").lt("start_time", matchEnd.toISOString()).gt("end_time", matchStart.toISOString());
          const bookedCourts = new Set((existing ?? []).map((b) => b.court_id));
          const freeCourt = (courts ?? []).find((c) => !bookedCourts.has(c.id));

          if (freeCourt) {
            // PR-AN: auto-book triggered by user joining last spot.
            // user_id stays = match.created_by (the activity organizer who
            // owns this booking). created_by = the session user whose action
            // triggered the auto-book (could be same or different).
            const { data: booking, error: bookErr } = await supabase
              .from("bookings")
              .insert({
                court_id: freeCourt.id, user_id: match.created_by,
                created_by: user?.id ?? match.created_by,
                start_time: matchStart.toISOString(), end_time: matchEnd.toISOString(),
                duration_minutes: durationMin, status: "confirmed" as const,
                is_open_match: true, activity_id: matchId, max_players: match.spots_total,
              })
              .select().single();

            if (booking && !bookErr) {
              // SPÅR 2-Pre: open_matches.booking_id dropped; only court_id needs update on open_matches.
              // activity_id was set on booking at INSERT (line above) — canonical link.
              await supabase.from("activities").update({ court_id: freeCourt.id }).eq("id", matchId);
              const { data: matchPlayers } = await supabase.from("activity_players").select("user_id").eq("activity_id", matchId).eq("status", "joined");
              if (matchPlayers) {
                await supabase.from("booking_players").insert(
                  matchPlayers.map((mp) => ({ booking_id: booking.id, user_id: mp.user_id, status: "accepted" as const }))
                );
              }
              toast.success(t("toast.matchFullAutoBooked", { court: freeCourt.name }));
              for (const mp of matchPlayers ?? []) {
                await supabase.from("notifications").insert({
                  user_id: mp.user_id,
                  organization_id: organization!.id,  // PR-B: org-scope per Phase 1 RLS
                  title: "Bana bokad!",
                  body: `Er aktivitet är full — ${freeCourt.name} är bokad ${frozenDateTime(matchStart)}.`,
                  type: "open_match_booked", data: { match_id: matchId, booking_id: booking.id },
                });
              }
            }
          } else {
            toast.warning(t("activities:toast.matchFullNoCourt"));
            await supabase.from("notifications").insert({
              user_id: match.created_by,
              organization_id: organization!.id,  // PR-B: org-scope per Phase 1 RLS
              title: "Ingen ledig bana",
              body: "Din aktivitet är full men det fanns ingen ledig bana. Kontakta hallen.",
              type: "open_match_no_court", data: { match_id: matchId },
            });
          }
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities"] });
      setConfirmMatch(null);
      toast.success(t("activities:toast.joinedMatch"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // 2d-ii Step 4.4: converged to leave_activity_v2 (atomic: status='left' + spots-1 clamp≥0 + delete
  // PENDING payment, gated on the player actually being joined — Lesson 59), replacing the old 2 non-
  // atomic client writes. Refuses a paid leave server-side (paid_leave_requires_refund); the card
  // "Lämna" button is gated on paidJoinSet so a paid player can't reach it, but map the error defensively.
  const leaveMutation = useMutation({
    mutationFn: async (matchId: string) => {
      const { error } = await (supabase.rpc as any)("leave_activity_v2", { p_activity_id: matchId });
      if (error) {
        const msg = error.message || "";
        if (/paid_leave_requires_refund/.test(msg)) {
          throw new Error(t("paidSpotContactOrganizer"));
        }
        throw new Error(msg || t("errors.couldNotLeave"));
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["activities"] }); toast.success(t("activities:toast.leftMatch")); },
    onError: (e: Error) => toast.error(e.message),
  });

  // 2e-core: self-service refund-leave for a PAID player inside the window (mirrors the detail
  // page). User-auth refund-activity-leave edge fn (money first → seat released via shared
  // _release_activity_seat). outside_window maps to the contact-organizer message defensively.
  const refundLeaveMutation = useMutation({
    mutationFn: async (matchId: string) => {
      const { data, error } = await supabase.functions.invoke("refund-activity-leave", {
        body: { activity_id: matchId },
      });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === "function") {
          const b = await ctx.json().catch(() => null) as { error?: string } | null;
          if (b?.error === "outside_window") throw new Error(t("activities:paidSpotContactOrganizer"));
        }
        throw new Error(t("activities:errors.couldNotLeave"));
      }
      return data as { seat_warning?: boolean } | null;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["my-paid-activity-joins"] });
      toast.success(res?.seat_warning
        ? t("activities:refundedSeatWarning", { defaultValue: "Återbetalat — kontakta arrangören om platsen står kvar" })
        : t("activities:refundedLeft", { defaultValue: "Du har lämnat aktiviteten — pengarna återbetalas" }));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Client-side filters
  let filtered: OpenMatchWithRelations[] = matches;
  if (levelFilter && profile?.level) {
    filtered = filtered.filter((m) => profile.level! >= (m.min_level ?? 1) && profile.level! <= (m.max_level ?? 10));
  }
  if (onlyJoinable && user) {
    filtered = filtered.filter((m) => {
      // T2v2/G-069 (decision 9): the creator's own un-joined activity IS
      // joinable for them — no creator exclusion here anymore.
      if (m.status !== "open") return false;
      const players = (m.activity_players ?? []).filter((p) => p.status === "joined");
      return !players.some((p) => p.user_id === user.id);
    });
  }

  if (isLoading) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full" />)}</div>;
  }
  if (filtered.length === 0) {
    return <p className="text-center text-muted-foreground py-12">{t('noOpenMatchesNow')}</p>;
  }

  const getActivityBadge = (match: OpenMatchWithRelations) => {
    const type = activityTypeMap.get(match.match_type);
    const icon = type?.icon ?? FALLBACK_BADGE.icon;
    const label = match.match_type === "custom" && match.activity_title
      ? match.activity_title
      : type?.label ?? match.match_type;
    const badgeStyle = type?.color
      ? { borderColor: `${type.color}33`, color: type.color }
      : undefined;
    return (
      <Badge variant="outline" className={`gap-1 ${type ? "" : FALLBACK_BADGE.badgeClass}`} style={badgeStyle}>
        <span className="text-xs">{icon}</span>
        {label}
      </Badge>
    );
  };

  return (
    <>
      <div className="space-y-3 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 lg:space-y-0">
        {filtered.map((match) => {
          const activePlayers = (match.activity_players ?? []).filter((p) => p.status === "joined");
          const isCreator = user?.id === match.created_by;
          const hasJoined = activePlayers.some((p) => p.user_id === user?.id);
          const spotsLeft = match.spots_total - match.spots_filled;
          const creator = profileMap[match.created_by];

          return (
            <Link key={match.id} to={`/activities/${match.id}`} className="block">
            <Card className="overflow-hidden hover:ring-1 hover:ring-primary/30 transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2 flex-1 min-w-0">
                    {/* Activity title */}
                    {match.activity_title && (
                      <p className="text-sm font-bold truncate">{match.activity_title}</p>
                    )}

                    {/* Badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {getActivityBadge(match)}
                      {match.status === "full" && (
                        <Badge variant="outline" className="text-muted-foreground">Full</Badge>
                      )}
                      {match.visibility === "private" && (
                        <Badge variant="outline" className="text-muted-foreground">Privat</Badge>
                      )}
                    </div>

                    {/* Creator info */}
                    {creator && (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="text-[10px]">{(creator.full_name ?? "?")[0]}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium">{creator.full_name ?? "Anonym"}</span>
                        <LevelBadge level={creator.level ?? 1} size="sm" />
                      </div>
                    )}

                    {/* Time & details */}
                    <div className="flex items-center gap-1.5 text-sm">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">{format(parseISO(match.date), "EEE d MMM", { locale: sv })}</span>
                      <span className="text-muted-foreground">{match.start_time?.slice(0, 5)}–{match.end_time?.slice(0, 5)}</span>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{match.courts?.name ?? "Valfri bana"}</span>
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{match.spots_filled}/{match.spots_total}</span>
                      <span>Nivå {match.min_level}–{match.max_level}</span>
                    </div>

                    {/* Price display */}
                    <ActivityPriceDisplay match={match} userId={user?.id} />

                    {/* Joined players */}
                    {activePlayers.length > 0 && (
                      <div className="flex items-center gap-1 pt-0.5">
                        {activePlayers.map((p) => (
                          <Avatar key={p.id} className="h-6 w-6 border-2 border-background -ml-1 first:ml-0">
                            <AvatarFallback className="text-[9px]">?</AvatarFallback>
                          </Avatar>
                        ))}
                        <span className="text-xs text-muted-foreground ml-1">
                          {spotsLeft > 0 ? `${spotsLeft} plats${spotsLeft > 1 ? "er" : ""} kvar` : "Full"}
                        </span>
                      </div>
                    )}

                    {match.description && (
                      <p className="text-xs text-muted-foreground italic">{match.description}</p>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="shrink-0 flex flex-col gap-1.5">
                    {/* T2v2/G-069 (decision 9): the join button renders for the
                        CREATOR too — no auto-join at create; the organizer
                        joins+pays like anyone else (join_activity_v2 has no
                        server-side creator block — recon-verified). */}
                    {!hasJoined && match.status === "open" && (
                      <Button size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmMatch(match); }} className="gap-1">
                        <UserPlus className="h-3.5 w-3.5" />{t("joinActivity")}
                      </Button>
                    )}
                    {hasJoined && (
                      <>
                        {/* 2e-core Policy-modell A (mirrors ActivityActions): PAID + inside window →
                            self-service refund-leave "Lämna (återbetalas X)"; PAID + outside → disabled
                            + contact-organizer; UNPAID → normal atomic leave. */}
                        {paidJoinSet.has(match.id) ? (
                          withinCancellationWindow(match) ? (
                            <Button size="sm" variant="outline" data-testid="refund-leave" onClick={(e) => { e.preventDefault(); e.stopPropagation(); refundLeaveMutation.mutate(match.id); }} disabled={refundLeaveMutation.isPending} className="gap-1 text-destructive">
                              <LogOut className="h-3.5 w-3.5" />
                              {paidJoinByMatch[match.id] != null && (paidJoinByMatch[match.id] as number) > 0
                                ? `${t("leaveActivity")} (${t("activities:refundedAmount", { defaultValue: "återbetalas {{amount}}", amount: formatPrice((paidJoinByMatch[match.id] as number) / 100) })})`
                                : t("leaveActivity")}
                            </Button>
                          ) : (
                            <>
                              <Button size="sm" variant="outline" disabled className="gap-1 text-destructive">
                                <LogOut className="h-3.5 w-3.5" />{t("leaveActivity")}
                              </Button>
                              <span className="text-[10px] leading-tight text-center text-muted-foreground" title={t("paidSpotContactOrganizer")}>{t("paidSpot")}</span>
                            </>
                          )
                        ) : (
                          <Button size="sm" variant="outline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); leaveMutation.mutate(match.id); }} disabled={leaveMutation.isPending} className="gap-1 text-destructive">
                            <LogOut className="h-3.5 w-3.5" />{t("leaveActivity")}
                          </Button>
                        )}
                      </>
                    )}
                    {isCreator && (
                      <div className="space-y-1">
                        <Badge variant="outline" className="text-xs">{t('yourActivity')}</Badge>
                        {(match.organizer_fee_per_person ?? 0) > 0 && match.organizer_payout_method === "hall_payout" && (
                          <Badge variant="outline" className={`text-[10px] block ${match.organizer_payout_status === "paid" ? "text-green-700" : "text-amber-700"}`}>
                            {match.organizer_payout_status === "paid" ? "✅ Utbetald" : "⏳ Inväntar utbetalning"}
                          </Badge>
                        )}
                        {(match.organizer_fee_per_person ?? 0) > 0 && match.organizer_payout_method === "swish_direct" && (
                          <Badge variant="outline" className="text-[10px] block text-muted-foreground">
                            Swish — deltagarna betalar direkt
                          </Badge>
                        )}
                      </div>
                    )}
                    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <ShareButton
                        entityType="open_match"
                        entityId={match.id}
                        title={match.activity_title || "Öppen match"}
                        subtitle={`${format(parseISO(match.date), "EEE d MMM", { locale: sv })} ${match.start_time?.slice(0, 5)}–${match.end_time?.slice(0, 5)}`}
                        className="h-8 w-8"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            </Link>
          );
        })}
      </div>

      {/* ─── Join confirmation dialog ─── */}
      <Dialog open={!!confirmMatch} onOpenChange={(open) => !open && setConfirmMatch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("joinConfirm", { name: confirmMatch?.activity_title || activityTypeMap.get(confirmMatch?.match_type ?? "")?.label || "aktivitet" })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {/* M1: the rows render the SAME canonical breakdown that gates and charges —
                Banhyra + Arrangörsavgift = Totalt by construction (composeJoinPriceMinor, öre).
                System-mode loading → skeleton; totalMinor 0 (free/external/court-less) → no
                price rows (M2 adds the external info-row). The old parallel rows
                (bookings.price_total flat split + usePlayerPrice category share + kr fee
                column) are gone. */}
            {confirmMatch?.payment_mode === "system" && !confirmBreakdown ? (
              <Skeleton className="h-16 w-full" />
            ) : confirmBreakdown && confirmBreakdown.totalMinor > 0 ? (
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>{t("courtRent", { defaultValue: "Banhyra" })}</span>
                  <span>{formatPrice({ valueMinor: confirmBreakdown.courtShareMinor, currency: "SEK" })}</span>
                </div>
                {confirmBreakdown.feeMinor > 0 && (
                  <div className="flex justify-between">
                    <span>{t("organizerFee", { defaultValue: "Arrangörsavgift" })}</span>
                    <span>{formatPrice({ valueMinor: confirmBreakdown.feeMinor, currency: "SEK" })}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-1">
                  <span>{t("total", { defaultValue: "Totalt" })}</span>
                  <span>{formatPrice({ valueMinor: confirmBreakdown.totalMinor, currency: "SEK" })}</span>
                </div>
              </div>
            ) : confirmMatch?.payment_mode === "external" ? (
              /* M2 (beslut A): external = ONE info-row from the fee column — the wizard's own label
                 defines the semantics ("Din avgift per person (tas utanför appen)", Step5Price). No
                 Banhyra/Totalt rows (charge is 0 in-app). fee 0/unset → no price row at all (C-style). */
              (confirmMatch.organizer_fee_per_person_minor ?? 0) > 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("externalFeeInfo", {
                    defaultValue: "Arrangörsavgift, betalas till arrangören utanför appen: {{amount}}",
                    amount: formatPrice({ valueMinor: confirmMatch.organizer_fee_per_person_minor!, currency: "SEK" }),
                  })}
                </p>
              ) : null
            ) : confirmMatch?.payment_mode === "free" ? (
              /* M2: free = its own info-state (no price rows; the old endless skeleton died in M1). */
              <p className="text-sm text-muted-foreground">{t("interestSignup", { defaultValue: "Intresseanmälan" })}</p>
            ) : null}
            {confirmMatch?.organizer_payout_method === "swish_direct" && confirmMatch?.organizer_swish_number && (
              <p className="text-xs text-muted-foreground">{t("swishOrganizerFee", { number: confirmMatch.organizer_swish_number })}</p>
            )}
            {confirmMatch?.organizer_payout_method === "hall_payout" && (
              <p className="text-xs text-muted-foreground">{t("allPaidToHall")}</p>
            )}
          </div>
          {/* 2d-ii Step 2: a no-card player whose join WILL be charged (>0) adds a saved card inline.
              Gated on the real charge amount (confirmWillCharge), NOT payment_mode — a 0-kr system
              join needs no card. No charge yet (Step 4). */}
          {confirmWillCharge && !hasMitCard && (
            <div className="space-y-2 border-t pt-3">
              <p className="text-sm font-medium">{t("addCardToJoin")}</p>
              <AddPaymentMethodInline onSaved={() => { /* saved-cards query invalidates → hasMitCard flips */ }} />
            </div>
          )}
          {confirmWillCharge && hasMitCard && (
            <p className="text-xs text-green-700 border-t pt-2 mt-2">{t("cardSaved")}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmMatch(null)}>{t("common:cancel")}</Button>
            {/* 2d-ii Step 4.3: when this join will charge (confirmWillCharge) and the player has no MIT
                card, "Gå med" is disabled — the inline add-card above (same confirmWillCharge && !hasMitCard
                condition) is the only way forward; saving a card flips hasMitCard → the button enables.
                Free/0-kr joins (confirmWillCharge false) stay ungated. Mirrors the detail-page gate. */}
            {/* B0: isJoinConfirmDisabled adds the loading gate (system-mode + breakdown unresolved →
                disabled) on top of the unchanged 4.3 card gate — mirrors the detail page's joinReady.
                Without it, confirmWillCharge reads false while the query loads and a no-card join
                slips through, creating an orphaned pending. */}
            <Button
              onClick={() => confirmMatch && joinMutation.mutate(confirmMatch.id)}
              disabled={isJoinConfirmDisabled({
                isPending: joinMutation.isPending,
                paymentMode: confirmMatch?.payment_mode,
                totalMinor: confirmBreakdown?.totalMinor,
                hasMitCard,
              })}
            >{t("joinActivity")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
